import React, { useState, useEffect, useCallback } from 'react';
import LoadingPage from '../../components/LoadingPage';
import { getApiClient, MunicipalityExtended } from '../../api';
import { Navigate, useNavigate } from 'react-router-dom';
import { PoliticalBodyCard } from '../../components/PoliticalBodyCard/PoliticalBodyCard';
import "./Bodies.css";
import { useTranslation } from 'react-i18next';
import { useLocalizationLabels } from '../../context/LocalizationLabelContext';
import { capitalizeFirstLetter } from '../../utils/texts';
import { useMunicipalityId } from '../../hooks/useCouncilPath';
import { buildCouncilPath } from '../../utils/councilParams';
import { logoUrl } from '../../utils/logoUrl';

export default function Organy() {
    const { t } = useTranslation();
    const municipalitaId = useMunicipalityId();
    const [municipality, setMunicipality] = useState<MunicipalityExtended | null>(null);
    const [error, setError] = useState<unknown>(null);
    const navigate = useNavigate();

    const {
        municipalityOptionLabels,
        municipalityNameOptionLabels,
        organNameOptionLabels
    } = useLocalizationLabels();

    const redirectToBody = useCallback(
        (bodyType: string, replace?: boolean) => {
            const fetchLatestCouncilId = async (bodyType: string) => {
                const api = getApiClient();
                const councilsData = await api.getCouncilsByMunicipalityIdAndBody(municipalitaId, bodyType);

                const latest = councilsData.reduce((maxPoradi: number, council: { poradi: number }) => {
                    return Math.max(maxPoradi, council.poradi);
                }, 0);

                return latest;
            };

            const redirectHomepage = async () => {
                try {
                    const latest = await fetchLatestCouncilId(bodyType);
                    navigate(buildCouncilPath(municipalitaId, bodyType, latest), { replace: !!replace });
                } catch (error) {
                    setError(error);
                }
            };

            redirectHomepage();
        },
        [municipalitaId, navigate]
    );

    useEffect(() => {
        const fetchData = async () => {
            try {
                const api = getApiClient();
                const municipalityData = await api.getMunicipalityByMunicipalityId(municipalitaId);
                if (Object.keys(municipalityData.organy).length === 1) {
                    redirectToBody(Object.keys(municipalityData.organy)[0], true);
                }
                setMunicipality(municipalityData);
            } catch (error) {
                setError(error);
            }
        };

        fetchData();
    }, [municipalitaId, redirectToBody]);

    if (municipality && Object.keys(municipality.organy).length > 1) {
        const accentColor = municipality.barva || '#6366f1';
        return (
            <div className='organy-municipality'>
                <div className='organy-municipality-wrapper'>
                    <div className='organy-municipality-hero'>
                        <img className='organy-municipality-logo' src={logoUrl(municipality.text_id)} alt='logo' />
                        <div>
                            <h1 className='organy-municipality-title' style={{ color: accentColor }}>
                                {t('Bodies.municipalityName', {
                                    type: capitalizeFirstLetter(municipalityOptionLabels[municipality.typ_code]?.label || municipality.typ_code),
                                    name: municipalityNameOptionLabels[municipality.id]?.label || municipality.text_id })}
                            </h1>
                            <p className='organy-municipality-description'>{t('Bodies.chooseBody')}</p>
                        </div>
                    </div>
                    <div className='organy-municipality-cards-grid'>
                        {Object.keys(municipality.organy)
                            .map((bodyType) => (
                                <PoliticalBodyCard
                                    key={`${municipality.id}-${bodyType}`}
                                    municipality={municipality}
                                    bodyType={bodyType}
                                    organNameOptionLabels={organNameOptionLabels}
                                    onClick={() => redirectToBody(bodyType)}
                                />
                            ))}
                    </div>
                </div>
            </div>
        );
    } else if (error) {
        return <Navigate to='/' />;
    } else {
        return <LoadingPage />;
    }
}
